// Package imports:
import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import Bugsnag from "@bugsnag/js";
// Service imports:
import { isSSN, stripSSN } from "../../../services/utils";
import { GET_KELDAN_API_URL } from "../../../services/config";
// Type imports:
import { AddCompanyFormValues } from "../../../types/DueDiligenceTypes";
import { IToastFromParent } from "../../../types/Types";

export const FormObserver: React.FC<IToastFromParent & {
    setShowLoading: React.Dispatch<React.SetStateAction<boolean>>
}> = ({
    setToastShow,
    setToastMessage,
    setToastType,
    setShowLoading
}) => {
        const { values, setFieldValue } = useFormikContext<AddCompanyFormValues>();
        const [lastCheckedSSN, setLastCheckedSSN] = useState("");

        const fetchCompanyBySSN = async (ssn: string): Promise<{ name: string, address: string } | null> => {
            try {
                setShowLoading(true);
                const cleanedSSN = stripSSN(ssn);
                const url = `${GET_KELDAN_API_URL()}/DueDiligence/SearchCompany?ssn=${cleanedSSN}`;

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    }
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error(await response.text());
                    }
                    throw new Error("Failed to fetch company data");
                }

                const data: { name: string, address: string } = await response.json();
                return data;
            } catch (error) {
                setToastType('alert');
                setToastMessage(error instanceof Error ? error.message : 'Óþekkt villa við að sækja upplýsingar um einstakling');
                setToastShow(true);
                console.error("Error fetching individual data:", error);
                return null;
            } finally {
                setShowLoading(false);
            }
        }

        useEffect(() => {
            const handleSSNChange = async () => {
                const ssn = values.companySSN;

                if (ssn === lastCheckedSSN || !isSSN(ssn)) {
                    return;
                }

                setLastCheckedSSN(ssn);

                if (!values.foreign) {
                    try {
                        const parsedFieldData = await fetchCompanyBySSN(ssn);
                        if (parsedFieldData?.name) {
                            setFieldValue("companyName", parsedFieldData.name);
                        }
                    } catch (e) {
                        if (e instanceof Error) Bugsnag.notify(e);
                    }
                }
            }

            handleSSNChange();
        }, [values.companySSN, lastCheckedSSN, setFieldValue]);

        return null;
    }